import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

// Cinematic scroll-triggered stagger reveal for a grid/list of items inside
// a container ref. Each item gets its own ScrollTrigger (rather than one
// trigger for the whole block) so long lists — like the experience timeline —
// reveal progressively as the user scrolls, instead of all firing on mount.
export const useStaggerReveal = (itemsSelector, options = {}) => {
  const {
    start = "top 85%",
    duration = 0.8,
    stagger = 0.12,
    ease = "power3.out",
    from = { opacity: 0, y: 60 },
    alternateX = false,
  } = options;

  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const ctx = gsap.context(() => {
      const items = gsap.utils.toArray(root.querySelectorAll(itemsSelector));
      items.forEach((item, i) => {
        const fromVars = alternateX
          ? { opacity: 0, x: i % 2 === 0 ? -80 : 80, y: 20 }
          : from;
        gsap.set(item, fromVars);
        ScrollTrigger.create({
          trigger: item,
          start,
          once: true,
          onEnter: () =>
            gsap.to(item, {
              opacity: 1,
              x: 0,
              y: 0,
              duration,
              delay: (i % 3) * (stagger / 2),
              ease,
            }),
        });
      });
    }, root);

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsSelector]);

  return containerRef;
};

// Animates a number (e.g. "30+", "6+") counting up from 0 when it scrolls
// into view. Preserves any non-numeric suffix like "+" or "%".
export const useCountUp = (endValue, options = {}) => {
  const { duration = 1.6, start = "top 90%" } = options;
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const match = String(endValue).match(/[\d.]+/);
    if (!match) {
      el.textContent = endValue;
      return undefined;
    }
    const num = parseFloat(match[0]);
    const suffix = String(endValue).slice(match.index + match[0].length);
    const isInt = Number.isInteger(num);
    const counter = { val: 0 };

    el.textContent = (isInt ? 0 : (0).toFixed(1)) + suffix;

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: el,
        start,
        once: true,
        onEnter: () =>
          gsap.to(counter, {
            val: num,
            duration,
            ease: "power2.out",
            onUpdate: () => {
              el.textContent = (isInt ? Math.floor(counter.val) : counter.val.toFixed(1)) + suffix;
            },
          }),
      });
    });

    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endValue]);

  return ref;
};
